import { ThemeProvider } from "@emotion/react";
import styled from "@emotion/styled";
import * as React from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Dispatch } from "redux";

import { Button, Textarea } from "@livechat/design-system-react-components";

import smileyHappy from "assets/svg/smiley-happy.svg";
import smileyOkay from "assets/svg/smiley-okay.svg";
import smileySad from "assets/svg/smiley-sad.svg";
import { ratingToTextMap } from "constants/rating";
import { EventPlace, trackEngagement, trackEngagementWaitForAmplitude } from "helpers/analytics";
import { Rating } from "interfaces/entities/ticket";
import { IStoreState } from "interfaces/storeState";
import { TicketActions } from "store/entities/tickets/actions";
import { isTicketRatingSending } from "store/no-auth/ticket-rating/selectors";
import { legacyTheme } from "themes/legacyTheme";

import PoweredBy from "./PoweredBy";
import { Container, Content, Header, Headline, Wrapper } from "./styled";

interface IMatchParams {
  rating: string;
  id: string;
  token: string;
}

interface IStateProps {
  ticketRatingSending: boolean;
}

interface IDispatchProps {
  sendTicketRating(id: string, token: string, rating: Rating, comment: string, submitted: boolean): void;
}

interface IProps extends IStateProps, IDispatchProps, RouteComponentProps<IMatchParams> {}

interface IState {
  rating: Rating | null;
  comment: string;
  submitClicked: boolean;
}

const Votes = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin: 40px 0;
  text-align: center;
`;

interface IStyledVote {
  active: boolean;
}

const StyledVote = styled.div<IStyledVote>`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  justify-content: center;
  margin: 0 6px;
  padding: 20px;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid
    ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => (props.active ? props.theme.linkColor : props.theme.borderColor)
    };
  border-radius: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.theme.borderRadius
  };
  ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.active && `box-shadow: ${props.theme.activeBoxShadow}`
  };

  @media (max-width: ${legacyTheme.mobileBreakpoint}) {
    padding: 12px;
  }

  &:first-child {
    margin-left: 0px;
  }
  &:last-child {
    margin-right: 0px;
  }

  &:hover {
    border-color: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.linkColor
    };
  }

  &:hover {
    border-color: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => (props.active ? props.theme.linkColor : props.theme.borderColorHover)
    };
  }

  span {
    display: block;
  }

  img {
    display: block;
    margin: 0 auto 14px;
    width: 40px;
    height: 40px;
  }
`;

const StyledTextArea = styled(Textarea)`
  margin-top: 0.5em;
  width: 100%;
  height: 8em;
  textarea {
    // TODO: Styles can be removed after DS fixes
    resize: none;
    width: 100%;
    max-width: 640px;
    box-sizing: border-box;
    height: 100%;
  }
`;

const ButtonWrapper = styled.div`
  margin-top: 20px;
  text-align: center;
`;

const availableRatings: string[] = [Rating.Good, Rating.Neutral, Rating.Bad];

function getDefaultRating(rating: string): Rating | null {
  return availableRatings.includes(rating) ? (rating as Rating) : null;
}

class TicketRating extends React.PureComponent<IProps, IState> {
  state = {
    rating: getDefaultRating(this.props.match.params.rating),
    comment: "",
    submitClicked: false,
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  componentDidMount() {
    if (this.state.rating) {
      this.sendRating(false);
    }

    trackEngagementWaitForAmplitude("Ticket rating page - Page entered", EventPlace.TicketRating, {
      rating: this.state.rating,
    });
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private handleVoteClick = (rating: Rating) => () => {
    this.setState({ rating });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ comment: e.target.value });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private sendRating = (submitted: boolean) => {
    const { rating, comment } = this.state;
    const { id, token } = this.props.match.params;
    if (!rating) {
      return;
    }
    this.props.sendTicketRating(id, token, rating, comment, submitted);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private handleSubmit = () => {
    this.setState({ submitClicked: true });
    this.sendRating(true);
    trackEngagement("Ticket rating page - Rating submitted", EventPlace.TicketRating, {
      rating: this.state.rating,
      commented: !!this.state.comment,
    });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render() {
    const { rating, comment, submitClicked } = this.state;
    const { ticketRatingSending } = this.props;
    const submitEnabled = !submitClicked || (Boolean(rating) && !ticketRatingSending);

    return (
      <ThemeProvider theme={legacyTheme}>
        <Container>
          <Wrapper>
            <Header>How did we do?</Header>
            <Headline>How was your support experience? We’ll appreciate your honest feedback!</Headline>
            <Content>
              <Votes>
                <StyledVote active={rating === Rating.Good} onClick={this.handleVoteClick(Rating.Good)}>
                  <img src={smileyHappy} />
                  <span>{ratingToTextMap[Rating.Good]}</span>
                </StyledVote>
                <StyledVote active={rating === Rating.Neutral} onClick={this.handleVoteClick(Rating.Neutral)}>
                  <img src={smileyOkay} />
                  <span>{ratingToTextMap[Rating.Neutral]}</span>
                </StyledVote>
                <StyledVote active={rating === Rating.Bad} onClick={this.handleVoteClick(Rating.Bad)}>
                  <img src={smileySad} />
                  <span>{ratingToTextMap[Rating.Bad]}</span>
                </StyledVote>
              </Votes>
              <p>Would you like to add a comment?</p>
              <StyledTextArea
                autoFocus
                maxLength={1000}
                placeholder="Your comment…"
                onChange={this.handleCommentChange}
                value={comment}
              />
              <ButtonWrapper>
                <Button
                  kind="primary"
                  size="large"
                  disabled={!submitEnabled}
                  onClick={this.handleSubmit}
                  loading={submitClicked && ticketRatingSending}
                  loaderLabel="Sending…"
                >
                  Send
                </Button>
              </ButtonWrapper>
            </Content>
            <PoweredBy />
          </Wrapper>
        </Container>
      </ThemeProvider>
    );
  }
}

const mapStateToProps = (state: IStoreState): IStateProps => ({
  ticketRatingSending: isTicketRatingSending(state),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  sendTicketRating: (id: string, token: string, rating: Rating, comment: string, submitted: boolean) =>
    // @ts-expect-error TS(2345)
    dispatch(TicketActions.sendRating({ id, token, rating, comment, submitted })),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TicketRating));
